import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { GeneralActions } from '@hunting-nature/Secacam/shared/redux/general/general.actions';
import { GeneralSelectors } from '@hunting-nature/Secacam/shared/redux/general/general.reducer';
import { LoadingController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { catchError, concat, EMPTY, distinctUntilChanged, filter, from, ignoreElements, map, of, switchMap, take, withLatestFrom, tap } from 'rxjs';

import { GeneralService } from '../../../../shared/services/general.service';
import { AlbumActions } from '../../albums/redux/album.actions';
import { albumListsSelector } from '../../albums/redux/album.reducer';
import { FilterByOtherCriteriaValues } from '../filter/other/other-filter.type';
import type { Album } from '@hunting-nature/shared-core/interfaces/album.interface';
import { GalleryPageService } from '../../services/gallery.page.service';
import { FilterActions } from '../filter/redux/filter.actions';
import { FilterSelectors } from '../filter/redux/filter.reducer';
import { GalleryService } from '../services/gallery.service';
import { ImageService } from '../services/image.service';

import { ToastService } from './../../../../shared/services/toast.service';
import { GalleryActions } from './gallery.actions';
import { GallerySelectors } from './gallery.reducer';
import { CamerasPageService } from '@hunting-nature/Secacam/pages/cameras/services/cameras.page.service';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';

@Injectable()
export class GalleryEffects {
  // services
  readonly actions$ = inject(Actions);
  readonly imageService = inject(ImageService);
  readonly store = inject(Store);
  readonly galleryService = inject(GalleryService);
  readonly generalService = inject(GeneralService);
  readonly galleryPageService = inject(GalleryPageService);
  readonly toastService = inject(ToastService);
  readonly loadingCtrl = inject(LoadingController);
  readonly camerasService = inject(CamerasPageService);
  readonly router = inject(Router);

  backButtonListener: PluginListenerHandle | null = null;

  /**
   * Unified entry: component dispatches `enterGalleryWithFilter` carrying the applied filter and optional relatedCameraId.
   * This effect sets the filter state, then triggers the full pipeline via `galleryEntered`. Keeps the data flow explicit — one action per user intent.
   */
  enterGalleryWithFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryWithFilterEntered),
      switchMap(({ applied, relatedCameraId }) => {
        const filterAction = FilterActions.applyCustomAlbumFilter({ filter: applied, relatedCameraId });
        return concat(of(filterAction), of(GalleryActions.galleryEntered({ filterActive: true })));
      })
    )
  );

  galleryEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryEntered),
      switchMap(({ filterActive }) =>
        this.imageService.initializeGallery$(filterActive).pipe(
          switchMap(({ initialImages, isCachedImages }) =>
            concat(
              // Phase 1: cached images land immediately — spinner stays up for
              // the remaining phases that follow.
              of(GalleryActions.loadOlderImagesSucceeded({ images: initialImages, filterActive, fetchedCount: initialImages.length })),

              // Phase 2: background refresh (only when we served from cache).
              // backgroundRefreshStarted → spinner up, loadNewerImages call → images update.
              isCachedImages
                ? concat(
                    of(GalleryActions.backgroundRefreshStarted()),
                    this.imageService.loadNewerImages$(filterActive).pipe(
                      switchMap(images => of(GalleryActions.backgroundNewerImagesLoaded({ images, filterActive }))),
                      catchError((error: unknown) => {
                        // Log but don't dispatch loadImagesFailure here — syncCompleted
                        // will clear the spinner at the end of the concat regardless.
                        console.error('Failed to load newer images on gallery enter', error);
                        return EMPTY;
                      })
                    )
                  )
                : EMPTY,

              // Phase 3: changes sync — only runs when a stored cursor exists.
              // On first visit images are already fresh, so there's nothing to reconcile.
              this.imageService.syncGalleryChanges$().pipe(
                switchMap(result => of(GalleryActions.syncChangesSucceeded({ ...result, filterActive }))),
                catchError((syncError: unknown) => {
                  console.error('Failed to sync changes on gallery enter', syncError);
                  return concat(
                    of(GalleryActions.syncChangesFailed({ error: syncError })),
                    from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
                  );
                })
              ),

              // All phases done — drop the spinner. This mirrors galleryRefreshed$ which
              // also dispatches syncCompleted after all steps complete, ensuring isSyncing
              // stays true through the entire chain.
              of(GalleryActions.syncCompleted())
            )
          ),
          catchError((error: unknown) => {
            console.error('Failed to initialize gallery', error);
            return concat(
              of(GalleryActions.galleryEnteredFailed({ error })),
              from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
            );
          })
        )
      )
    )
  );

  loadNewerImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.loadNewerImages),
      switchMap(({ filterActive }) => {
        return this.imageService.loadNewerImages$(filterActive).pipe(
          switchMap(images => of(GalleryActions.loadNewerImagesSucceeded({ images, filterActive }))),
          catchError((error: unknown) => {
            console.error('Failed to load newer images', error);
            return concat(
              of(GalleryActions.loadImagesFailure()),
              from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
            );
          })
        );
      })
    )
  );

  /**
   * Pull-to-refresh: loads newer images AND runs the full changes-sync in sequence,
   * so every visible sync trigger always does the same thing — load new + check for
   * updates/deletions in the cached window. Dispatches syncCompleted at the end so
   * the page's completion effect can drop the refresher spinner (it waits for both).
   */
  galleryRefreshed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryRefreshed),
      switchMap(({ filterActive }) =>
        concat(
          // Reducer sets isSyncing: true on loadNewerImages dispatch, so the spinner stays up.
          this.imageService.loadNewerImages$(filterActive).pipe(
            switchMap(images => of(GalleryActions.loadNewerImagesSucceeded({ images, filterActive }))),
            catchError((error: unknown) => {
              console.error('Failed to load newer images on refresh', error);
              return concat(
                of(GalleryActions.loadImagesFailure()),
                from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
              );
            })
          ),
          this.imageService.syncGalleryChanges$().pipe(
            switchMap(result => of(GalleryActions.syncChangesSucceeded({ ...result, filterActive }))),
            catchError((syncError: unknown) => {
              console.error('Failed to sync changes on refresh', syncError);
              return concat(
                of(GalleryActions.syncChangesFailed({ error: syncError })),
                from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
              );
            })
          ),
          // Signal the page that both chains are done — completion effect drops the spinner.
          of(GalleryActions.syncCompleted())
        )
      )
    )
  );

  loadOlderImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.loadOlderImages),
      withLatestFrom(
        this.store.select(GallerySelectors.loadedAllOlderImages),
        this.store.select(GallerySelectors.paginationAnchor),
        this.store.select(GallerySelectors.paginationOffset)
      ),
      filter(([_, loadedAllOlderImages]) => !loadedAllOlderImages),
      switchMap(([{ filterActive }, _, paginationAnchor, paginationOffset]) =>
        this.imageService.loadOlderImages$({ filterActive, paginationAnchor, paginationOffset }).pipe(
          switchMap(({ images, filteredCount, fromCache }) =>
            concat(
              of(GalleryActions.loadOlderImagesSucceeded({ images, filterActive, fetchedCount: filteredCount })),
              !fromCache && filteredCount < this.imageService.loadImagesCount() ? of(GalleryActions.lastOlderImagesLoaded()) : EMPTY
            )
          ),
          catchError((error: unknown) => {
            console.error('Failed to load older images', error);
            return concat(
              of(GalleryActions.loadImagesFailure()),
              from(this.toastService.showTranslatedError$('gallery.sync.status.error')).pipe(ignoreElements())
            );
          })
        )
      )
    )
  );

  refreshFilteredImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.clearFilteredImages),
      withLatestFrom(this.store.select(FilterSelectors.isParametersApplied)),
      switchMap(([_, filterActive]) => of(GalleryActions.loadOlderImages({ filterActive })))
    )
  );

  refreshFilteredResultsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FilterActions.applyDetectionsFilter,
        FilterActions.applyCamerasFilter,
        FilterActions.applyOthersFilter,
        FilterActions.applyTimeDateFilter,
        FilterActions.applyCustomAlbumFilter,
        FilterActions.clearDetections,
        FilterActions.clearCameras,
        FilterActions.clearPeriod,
        FilterActions.clearOthers,
        FilterActions.clearAll,
        FilterActions.clearAllPreserveCameraLock
      ),
      map(() => GalleryActions.filteredResultsCountRequested())
    )
  );

  loadFilteredResultsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.filteredResultsCountRequested),
      withLatestFrom(this.store.select(FilterSelectors.applied)),
      switchMap(([_, applied]) =>
        this.imageService.fetchFilteredResultsCount$(applied).pipe(
          map(count => GalleryActions.filteredResultsCountLoaded({ count })),
          catchError((error: unknown) => {
            console.error('Failed to load filtered results count', error);
            return of(GalleryActions.filteredResultsCountFailed({ error }));
          })
        )
      )
    )
  );

  downloadSelectedImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.downloadSelectedImages),
      switchMap(action => {
        return this.galleryService.saveImagesToAlbum$(action.images, false, 'Secacam', 'secacam').pipe(
          map(_result => GalleryActions.downloadSelectedImagesSucceeded()),
          catchError((error: unknown) => {
            console.error(error);
            return of(
              GalleryActions.downloadSelectedImagesFailed({
                //error: DOWNLOAD_FILE_ERROR.GENERIC,
                error: error,
              })
            );
          })
        );
      })
    )
  );

  shareSelectedImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.shareSelectedImages),
      switchMap(action => {
        return this.galleryService
          .shareImages$(
            action.images.filter(image => image?.link),
            false
          )
          .pipe(
            map(_result => GalleryActions.shareSelectedImagesSucceeded()),
            catchError((error: unknown) => {
              console.error(error);
              return of(
                GalleryActions.shareSelectedImagesFailed({
                  //error: DOWNLOAD_FILE_ERROR.GENERIC,
                  error: error,
                })
              );
            })
          );
      })
    )
  );

  markSelectedImagesAsFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.markSelectedImagesAsFavorite),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  markImageAsFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.markImageAsFavorite),
      map(() => GeneralActions.openComingSoonModal())
    )
  );

  deleteSelectedImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.deleteSelectedImages),
      withLatestFrom(this.store.select(GallerySelectors.sharedCameras)),
      map(([action, sharedCameras]) => ({
        images: action.images.filter(image =>
          sharedCameras ? sharedCameras.find(camera => camera.imei === image.imei)?.permission !== PermissionValues.VIEWER : true
        ),
        filterActive: action.filterActive,
      })),
      switchMap(action => {
        if (action.images.length === 0) {
          return of(
            GalleryActions.deleteSelectedImagesFailed({
              error: 'No images to delete. Images with Viewer permission cannot be deleted.',
            })
          );
        }
        return this.galleryService.deleteImages$(action.images.map(image => image.id)).pipe(
          map((imageIds: Array<string>) => GalleryActions.deleteSelectedImagesSucceeded({ imageIDs: imageIds, filterActive: action.filterActive })),
          catchError((error: HttpErrorResponse) => {
            console.error(error);
            return of(
              GalleryActions.deleteSelectedImagesFailed({
                error: error.message,
              })
            );
          })
        );
      })
    )
  );

  // Image Viewer Effects
  // These belong here because Gallery owns the images array being viewed
  openImageViewer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.openImageViewer),
        switchMap(() => this.generalService.unlockScreenOrientation$()),
        switchMap(async () => {
          this.backButtonListener = await App.addListener('backButton', () => {
            this.store.dispatch(AlbumActions.closeImageViewer());
          });
        })
      ),
    { dispatch: false }
  );

  private removeBackButtonListener$(): Observable<void> {
    if (!this.backButtonListener) {
      return of(undefined);
    }

    return from(this.backButtonListener.remove()).pipe(
      tap(() => {
        this.backButtonListener = null;
      })
    );
  }

  closeImageViewer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AlbumActions.closeImageViewer, AlbumActions.closeImageViewerAndOpenCameraDetails),
        switchMap(() => this.removeBackButtonListener$().pipe(switchMap(() => this.galleryService.closeImageViewer$())))
      ),
    { dispatch: false }
  );

  // TODO: imageViewerSlideChanged$ — removed during merge, references
  // BlobPool / blobService / imageWorker that don't exist yet. Re-add once
  // the blob pre-loading infrastructure lands.

  enterGalleryPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryPageEntered),
      switchMap(() =>
        this.camerasService.getCameras$().pipe(
          map(({ ownCameras, sharedCameras, servicePlanCameras }) =>
            GalleryActions.galleryPageEnteredSucceeded({ ownCameras, sharedCameras, servicePlanCameras })
          ),
          catchError((error: unknown) => {
            console.error('Failed to enter gallery page', error);
            return of(GalleryActions.galleryPageEnteredFailed({ error }));
          })
        )
      )
    )
  );

  /**
   * Album resolution for deep links / page refreshes. The store's album
   * lists may still contain placeholders (initial state) on a hard refresh,
   * so first wait for — or trigger — the album load (ensureAlbumsLoaded$),
   * then resolve the albumId against user-defined and camera albums.
   */
  resolveGalleryAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryAlbumRequested),
      switchMap(({ albumId }) =>
        // Emit an object of all three lists once they are populated (first
        // emission where neither list is still in placeholder state).
        this.store.select(albumListsSelector).pipe(
          filter(({ userDefinedAlbums, ownCameras }) => !this.isPlaceholderList(userDefinedAlbums) && !this.isPlaceholderList(ownCameras)),
          take(1),
          switchMap(({ userDefinedAlbums, ownCameras, sharedWithUser }) => {
            const resolvedAlbum = this.resolveAlbum(albumId, userDefinedAlbums, ownCameras, sharedWithUser);
            if (resolvedAlbum.type !== GalleryActions.galleryAlbumNotFound.type || this.isPredefinedAlbumRoute(albumId)) {
              return of(resolvedAlbum);
            }

            return this.camerasService.getCameraById$(albumId).pipe(
              map(camera =>
                GalleryActions.galleryAlbumLoaded({
                  applied: { cameras: [albumId] },
                  relatedCameraId: albumId,
                  title: camera.name,
                })
              ),
              catchError(() => of(resolvedAlbum))
            );
          })
        )
      )
    )
  );

  /** Ensures albums are loaded before resolveGalleryAlbum$ can see them.
   *  Self-registering effect (subscribes via EffectsModule) — never called explicitly. */
  ensureAlbumsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryAlbumRequested),
      withLatestFrom(this.store.select(albumListsSelector)),
      filter(([, { userDefinedAlbums, ownCameras }]) => this.isPlaceholderList(userDefinedAlbums) || this.isPlaceholderList(ownCameras)),
      map(() => AlbumActions.albumsPageEntered())
    )
  );

  /** Album found — feeds the unified filter-entering pipeline. */
  galleryAlbumLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryAlbumLoaded),
      map(({ applied, relatedCameraId, title }) => GalleryActions.galleryWithFilterEntered({ applied, relatedCameraId, title }))
    )
  );

  /**
   * Album not found (e.g. albumId 'all') — fall back to the unfiltered
   * "all images" view, mirroring the previous in-component fallback.
   * `filterActive` is hardcoded to `false` here (not read from the store) because
   * `clearAll` — dispatched just before — guarantees that by the time `galleryEntered`
   * is processed. Reading it beforehand via `withLatestFrom` would capture the stale
   * pre-clear value, misrouting the fetched images into the wrong pool.
   */
  galleryAlbumNotFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GalleryActions.galleryAlbumNotFound),
      switchMap(() => concat(of(FilterActions.clearAll()), of(GalleryActions.galleryEntered({ filterActive: false }))))
    )
  );

  galleryEnterOfflineMode$ = createEffect(() =>
    this.store.select(GeneralSelectors.isOfflineMode).pipe(
      distinctUntilChanged(),
      filter(isOffline => isOffline),
      withLatestFrom(this.store.select(GallerySelectors.isSelectionModeActive)),
      filter(([_, isSelectionModeActive]) => isSelectionModeActive),
      map(() => GalleryActions.exitSelectionMode())
    )
  );

  /**
   * Resolves a gallery albumId to its filter + title. Custom (user-defined)
   * albums map their stored filter; camera albums (own or shared) filter by
   * camera id. Falls back to `galleryAlbumNotFound` for everything else.
   */
  private resolveAlbum(albumId: string, userDefinedAlbums: Array<Album>, ownCameras: Array<Album>, sharedWithUser: Array<Album>) {
    const otherCriteriaTags: Array<string> = FilterByOtherCriteriaValues;

    const customAlbum = userDefinedAlbums.find(album => album.id === albumId);
    if (customAlbum !== undefined) {
      return GalleryActions.galleryAlbumLoaded({
        applied: {
          detections: customAlbum.filter?.tags?.oneOf?.filter(tag => !otherCriteriaTags.includes(tag)),
          cameras: customAlbum.filter?.collection?.cameraIds,
          period: customAlbum.filter?.date,
          others: customAlbum.filter?.tags?.allOf?.filter(tag => otherCriteriaTags.includes(tag)),
        },
        title: customAlbum.name ?? albumId,
      });
    }

    const cameraAlbum = ownCameras.find(album => album.id === albumId) ?? sharedWithUser.find(album => album.id === albumId);
    if (cameraAlbum !== undefined) {
      return GalleryActions.galleryAlbumLoaded({
        applied: { cameras: [albumId] },
        relatedCameraId: albumId,
        title: cameraAlbum.name,
      });
    }

    return GalleryActions.galleryAlbumNotFound({ albumId });
  }

  private isPredefinedAlbumRoute(albumId: string): boolean {
    return albumId === 'all' || albumId === 'favorites';
  }

  /**
   * The album reducers seed their lists with placeholder entries (name === '').
   * A list containing a placeholder has not been fetched yet in this app
   * session. An empty list is a valid fetched result.
   */
  private isPlaceholderList(albums: Array<Album> | undefined): boolean {
    return !albums || albums.some(album => album.name === '');
  }
}
